import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const CLASSES = ["owner", "group", "other"];
const PERMS = ["read", "write", "execute"];

const PERM_BIT = { read: 4, write: 2, execute: 1 };

const SPECIAL_BITS = [
  { key: "setuid", value: 4 },
  { key: "setgid", value: 2 },
  { key: "sticky", value: 1 },
];

const PRESETS = [
  { key: "644", octal: "0644" },
  { key: "664", octal: "0664" },
  { key: "755", octal: "0755" },
  { key: "775", octal: "0775" },
  { key: "600", octal: "0600" },
  { key: "700", octal: "0700" },
  { key: "777", octal: "0777" },
];

const emptyMatrix = () => ({
  owner: { read: false, write: false, execute: false },
  group: { read: false, write: false, execute: false },
  other: { read: false, write: false, execute: false },
});

const emptySpecial = () => ({ setuid: false, setgid: false, sticky: false });

const matrixToDigits = (matrix) =>
  CLASSES.map((cls) =>
    PERMS.reduce((sum, p) => (matrix[cls][p] ? sum + PERM_BIT[p] : sum), 0),
  );

const specialDigit = (special) =>
  SPECIAL_BITS.reduce((sum, b) => (special[b.key] ? sum + b.value : sum), 0);

const digitToPerms = (digit) => ({
  read: (digit & 4) === 4,
  write: (digit & 2) === 2,
  execute: (digit & 1) === 1,
});

// Parse a string of 3 or 4 octal digits ("755" or "0755" or "4755") into matrix + special.
const parseOctal = (raw) => {
  const cleaned = raw.replace(/^0o/i, "").replace(/^0+(?=\d)/, "");
  const padded = cleaned === "" ? "0" : cleaned;
  if (!/^[0-7]{1,4}$/.test(padded)) return null;
  const padded4 = padded.padStart(4, "0");
  const [s, o, g, ot] = padded4.split("").map((c) => parseInt(c, 10));
  const matrix = {
    owner: digitToPerms(o),
    group: digitToPerms(g),
    other: digitToPerms(ot),
  };
  const special = {
    setuid: (s & 4) === 4,
    setgid: (s & 2) === 2,
    sticky: (s & 1) === 1,
  };
  return { matrix, special };
};

// Build the 9-character symbolic string (e.g. rwxr-xr-x), folding in special bits
// the way `ls -l` displays them (s/S replaces x for setuid/setgid; t/T replaces x
// on "other").
const toSymbolic = (matrix, special) => {
  const out = [];
  CLASSES.forEach((cls, idx) => {
    const p = matrix[cls];
    out.push(p.read ? "r" : "-");
    out.push(p.write ? "w" : "-");
    let xChar = p.execute ? "x" : "-";
    if (idx === 0 && special.setuid) xChar = p.execute ? "s" : "S";
    if (idx === 1 && special.setgid) xChar = p.execute ? "s" : "S";
    if (idx === 2 && special.sticky) xChar = p.execute ? "t" : "T";
    out.push(xChar);
  });
  return out.join("");
};

// Parse 9-char symbolic notation back into matrix + special (loose: accepts -/r/w/x/s/S/t/T).
const parseSymbolic = (raw) => {
  const trimmed = raw.trim();
  if (!/^[-rwxsStT]{9}$/.test(trimmed)) return null;
  const matrix = emptyMatrix();
  const special = emptySpecial();
  CLASSES.forEach((cls, idx) => {
    const r = trimmed[idx * 3];
    const w = trimmed[idx * 3 + 1];
    const x = trimmed[idx * 3 + 2];
    matrix[cls].read = r === "r";
    matrix[cls].write = w === "w";
    if (x === "x") matrix[cls].execute = true;
    else if (x === "s" || x === "S") {
      matrix[cls].execute = x === "s";
      if (idx === 0) special.setuid = true;
      if (idx === 1) special.setgid = true;
    } else if (x === "t" || x === "T") {
      matrix[cls].execute = x === "t";
      if (idx === 2) special.sticky = true;
    }
  });
  return { matrix, special };
};

const ChmodCalculator = () => {
  const { t } = useTranslation();
  const [matrix, setMatrix] = useState(() => {
    const init = parseOctal("0644");
    return init ? init.matrix : emptyMatrix();
  });
  const [special, setSpecial] = useState(emptySpecial);
  const [octalInput, setOctalInput] = useState("644");
  const [symbolicInput, setSymbolicInput] = useState("rw-r--r--");
  const [octalError, setOctalError] = useState("");
  const [symbolicError, setSymbolicError] = useState("");

  const digits = useMemo(() => matrixToDigits(matrix), [matrix]);
  const specialD = useMemo(() => specialDigit(special), [special]);
  const hasSpecial = specialD > 0;
  const octal3 = digits.join("");
  const octal4 = `${specialD}${octal3}`;
  const displayOctal = hasSpecial ? octal4 : octal3;
  const symbolic = useMemo(() => toSymbolic(matrix, special), [matrix, special]);

  const warnings = useMemo(() => {
    const list = [];
    const worldWritable = matrix.other.write;
    const groupWritable = matrix.group.write;
    const hasSuidBit = special.setuid || special.setgid;
    if (hasSuidBit && (worldWritable || groupWritable)) {
      list.push("suid_writable");
    } else if (worldWritable) {
      list.push("world_writable");
    }
    return list;
  }, [matrix, special]);

  const togglePerm = (cls, perm) => {
    setMatrix((prev) => ({
      ...prev,
      [cls]: { ...prev[cls], [perm]: !prev[cls][perm] },
    }));
    setOctalError("");
    setSymbolicError("");
    const next = {
      ...matrix,
      [cls]: { ...matrix[cls], [perm]: !matrix[cls][perm] },
    };
    setOctalInput(matrixToDigits(next).join(""));
    setSymbolicInput(toSymbolic(next, special));
  };

  const toggleSpecial = (key) => {
    setSpecial((prev) => ({ ...prev, [key]: !prev[key] }));
    const nextSpecial = { ...special, [key]: !special[key] };
    const nextSpecialDigit = specialDigit(nextSpecial);
    setOctalInput(
      nextSpecialDigit > 0
        ? `${nextSpecialDigit}${matrixToDigits(matrix).join("")}`
        : matrixToDigits(matrix).join(""),
    );
    setSymbolicInput(toSymbolic(matrix, nextSpecial));
    setOctalError("");
    setSymbolicError("");
  };

  const applyOctal = (raw) => {
    setOctalInput(raw);
    const parsed = parseOctal(raw);
    if (!parsed) {
      setOctalError(t("chmod_calculator_invalid_octal"));
      return;
    }
    setOctalError("");
    setMatrix(parsed.matrix);
    setSpecial(parsed.special);
    setSymbolicInput(toSymbolic(parsed.matrix, parsed.special));
    setSymbolicError("");
  };

  const applySymbolic = (raw) => {
    setSymbolicInput(raw);
    const parsed = parseSymbolic(raw);
    if (!parsed) {
      setSymbolicError(t("chmod_calculator_invalid_symbolic"));
      return;
    }
    setSymbolicError("");
    setMatrix(parsed.matrix);
    setSpecial(parsed.special);
    const sd = specialDigit(parsed.special);
    setOctalInput(
      sd > 0
        ? `${sd}${matrixToDigits(parsed.matrix).join("")}`
        : matrixToDigits(parsed.matrix).join(""),
    );
    setOctalError("");
  };

  const applyPreset = (octal) => applyOctal(octal);

  const reset = () => applyOctal("0000");

  const copy = async (value, label) => {
    if (!value) {
      toast.error(t("chmod_calculator_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const command = `chmod ${displayOctal} <file>`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("chmod_calculator_title")}
            </h1>
            <p className="text-gray-600">{t("chmod_calculator_intro")}</p>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-gray-700">
                      <th className="text-left py-1 pr-3 font-medium">
                        {t("chmod_calculator_class_label")}
                      </th>
                      <th className="text-center py-1 px-2 font-medium">
                        {t("chmod_calculator_perm_read")}
                        <span className="ml-1 font-mono text-gray-500">(r=4)</span>
                      </th>
                      <th className="text-center py-1 px-2 font-medium">
                        {t("chmod_calculator_perm_write")}
                        <span className="ml-1 font-mono text-gray-500">(w=2)</span>
                      </th>
                      <th className="text-center py-1 px-2 font-medium">
                        {t("chmod_calculator_perm_execute")}
                        <span className="ml-1 font-mono text-gray-500">(x=1)</span>
                      </th>
                      <th className="text-center py-1 px-2 font-medium">
                        {t("chmod_calculator_octal_label")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLASSES.map((cls, idx) => (
                      <tr
                        key={cls}
                        className="border-t border-indigo-100"
                      >
                        <td className="py-2 pr-3 font-medium text-gray-700">
                          {t(`chmod_calculator_class_${cls}`)}
                        </td>
                        {PERMS.map((perm) => (
                          <td key={perm} className="text-center py-2 px-2">
                            <input
                              type="checkbox"
                              checked={matrix[cls][perm]}
                              onChange={() => togglePerm(cls, perm)}
                              aria-label={`${t(`chmod_calculator_class_${cls}`)} ${t(`chmod_calculator_perm_${perm}`)}`}
                              className="h-4 w-4"
                            />
                          </td>
                        ))}
                        <td className="text-center py-2 px-2 font-mono text-gray-800">
                          {digits[idx]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-700">
                  {t("chmod_calculator_special_label")}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {SPECIAL_BITS.map((b) => (
                    <label
                      key={b.key}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={special[b.key]}
                        onChange={() => toggleSpecial(b.key)}
                        className="h-4 w-4 mt-0.5"
                      />
                      <span>
                        <span className="font-medium">
                          {t(`chmod_calculator_special_${b.key}`)}
                        </span>
                        <span className="ml-1 font-mono text-gray-500">
                          ({b.value})
                        </span>
                        <span className="block text-xs text-gray-500">
                          {t(`chmod_calculator_special_${b.key}_help`)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("chmod_calculator_presets_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <StandardButton
                    key={p.key}
                    onClick={() => applyPreset(p.octal)}
                    type={octal3 === p.key && !hasSpecial ? "primary" : "secondary"}
                    variant={octal3 === p.key && !hasSpecial ? undefined : "outlined"}
                    size="small"
                  >
                    <span className="font-mono mr-1">{p.key}</span>
                    {t(`chmod_calculator_preset_${p.key}`)}
                  </StandardButton>
                ))}
                <StandardButton
                  onClick={reset}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("chmod_calculator_reset")}
                </StandardButton>
              </div>
            </div>

            {warnings.length > 0 && (
              <div
                role="alert"
                aria-live="polite"
                className={`p-3 rounded-md border text-sm ${
                  warnings.includes("suid_writable")
                    ? "bg-red-50 border-red-200 text-red-700"
                    : "bg-amber-50 border-amber-200 text-amber-800"
                }`}
              >
                <div className="font-medium">
                  {t("chmod_calculator_warning_title")}
                </div>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {warnings.map((key) => (
                    <li key={key}>{t(`chmod_calculator_warning_${key}`)}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("chmod_calculator_octal_input_label")}
                </label>
                <input
                  type="text"
                  value={octalInput}
                  onChange={(e) => applyOctal(e.target.value)}
                  placeholder="e.g. 755 or 4755"
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                {octalError && (
                  <span className="text-xs text-red-600">{octalError}</span>
                )}
                <span className="text-xs text-gray-500">
                  {t("chmod_calculator_octal_help")}
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("chmod_calculator_symbolic_input_label")}
                </label>
                <input
                  type="text"
                  value={symbolicInput}
                  onChange={(e) => applySymbolic(e.target.value)}
                  placeholder="rwxr-xr-x"
                  className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                {symbolicError && (
                  <span className="text-xs text-red-600">{symbolicError}</span>
                )}
                <span className="text-xs text-gray-500">
                  {t("chmod_calculator_symbolic_help")}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white border border-gray-200 rounded-md p-3 flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  {t("chmod_calculator_result_octal")}
                </span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-lg text-indigo-700">
                    {displayOctal}
                  </span>
                  <StandardButton
                    onClick={() =>
                      copy(displayOctal, t("chmod_calculator_result_octal"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-md p-3 flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  {t("chmod_calculator_result_symbolic")}
                </span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-lg text-indigo-700">
                    {symbolic}
                  </span>
                  <StandardButton
                    onClick={() =>
                      copy(symbolic, t("chmod_calculator_result_symbolic"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-md p-3 flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  {t("chmod_calculator_result_command")}
                </span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm text-gray-800 truncate">
                    {command}
                  </span>
                  <StandardButton
                    onClick={() =>
                      copy(command, t("chmod_calculator_result_command"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
              <div className="font-medium mb-1">
                {t("chmod_calculator_summary_label")}
              </div>
              <ul className="list-disc list-inside space-y-1">
                {CLASSES.map((cls) => {
                  const granted = PERMS.filter((p) => matrix[cls][p]);
                  return (
                    <li key={cls}>
                      <span className="font-medium">
                        {t(`chmod_calculator_class_${cls}`)}:
                      </span>{" "}
                      {granted.length === 0
                        ? t("chmod_calculator_summary_none")
                        : granted
                            .map((p) => t(`chmod_calculator_perm_${p}`))
                            .join(", ")}
                    </li>
                  );
                })}
                {hasSpecial && (
                  <li>
                    <span className="font-medium">
                      {t("chmod_calculator_special_label")}:
                    </span>{" "}
                    {SPECIAL_BITS.filter((b) => special[b.key])
                      .map((b) => t(`chmod_calculator_special_${b.key}`))
                      .join(", ")}
                  </li>
                )}
              </ul>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("chmod_calculator_note_octal")}</p>
              <p>{t("chmod_calculator_note_symbolic")}</p>
              <p>{t("chmod_calculator_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ChmodCalculator;
